import { type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
import styled, { css, useTheme } from "styled-components";

import {
  controlRadius,
  inlineElementHeight,
  inlineElementIndicatorSize,
  inlineElementTypography,
} from "../foundations/controlStyles";
import {
  interactiveSurfaceCss,
  type InteractiveSurfaceSpec,
} from "../foundations/interactiveSurface";
import { dataVizKeyOf, type DataVizToneName } from "../../theme/dataVizPalette";
import { Text, type TextVariantDefinition } from "../Text";
import { TruncatedText } from "../TruncatedText";

export type LabelChipSize = "xs" | "sm" | "md";
/**
 * `filled`=tint 배경·테두리 없음, `outline`=tint 배경 + 테두리(둘 다 정적 `<span>`).
 * `interactive`=클릭형 칩 — 네이티브 `<button>` 으로 렌더해 키보드/포커스/disabled 를 네이티브로
 * 얻고, hover/active/focus/disabled 진행은 IconButton 과 공유하는 interactiveSurfaceCss 로 입힌다
 * (평소 filled 외관 → hover 시 배경이 진해지고 칩 색 테두리가 뜸).
 */
export type LabelChipVariant = "filled" | "outline" | "interactive";
export type LabelChipIconPosition = "left" | "right";

export interface LabelChipProps extends Omit<HTMLAttributes<HTMLElement>, "children" | "color"> {
  /** Numeric RGB for user-authored labels; tone takes precedence when provided. */
  color?: number;
  /** Live semantic category colour, or neutral for labels without a category hue. */
  tone?: DataVizToneName | "neutral";
  /** Interactive toggle state. Omit to retain action-chip styling and semantics. */
  selected?: boolean;
  children: ReactNode;
  size?: LabelChipSize;
  variant?: LabelChipVariant;
  iconPosition?: LabelChipIconPosition;
  /**
   * 칩 아이콘. 지정하면 컬러 swatch 대신 이 노드를 렌더한다(swatch 가 칩의 기본 "아이콘"이므로
   * 임의 아이콘으로 대체). 미지정이면 `showIcon` 에 따라 swatch 를 그린다.
   */
  icon?: ReactNode;
  /** 컬러 swatch 표시 여부(아이콘 미지정 시에만 의미). false면 텍스트만 남는 칩이 된다. */
  showIcon?: boolean;
  /**
   * `interactive` variant 한정 — 클릭형 칩을 비활성화한다(네이티브 `<button>` 의 disabled:
   * 포인터/키보드 차단 + disabled 외관). static(filled/outline) 칩에는 의미 없다.
   */
  disabled?: boolean;
}

type LabelChipColors = {
  accent: string;
  background: string;
  border: string;
  backgroundHover: string;
  backgroundActive: string;
  backgroundMuted: string;
  borderHover: string;
  borderActive: string;
};

const maxRgbColor = 0xffffff;
const defaultLabelChipColor = 0x3366cc;
// 라벨은 평문 가중치로 — bold 는 칩 안에서 글자가 위로 떠 보이는 인상을 키워 제거(intent: 차분한 라벨).
const labelWeight = "var(--c6-typography-weight-regular)";
// interactive + disabled 일 때 라벨 텍스트를 mute — Text 가 자체 color(text.default)를 박아
// 버튼 color 상속을 막으므로, 인라인으로 disabled 토큰을 직접 내려 덮는다.
const disabledTextStyle: CSSProperties = { color: "var(--c6-color-text-disabled)" };

const Swatch = styled.span`
  display: block;
  flex: 0 0 auto;
  /* 스와치 윤곽은 테마 잉크(text.default) 알파 — 라이트에선 어두운, 다크에선 밝은 테두리가
     되어 칩 틴트·surface 와 톤이 같아도 원색 스와치가 살아남는다(구 검정 고정 보더는 다크에서
     소실). 흰색 inset 하이라이트는 다크 네이비 테마에서 이질적이라 제거. */
  border: 1px solid color-mix(in srgb, var(--c6-color-text-default) 22%, transparent);
`;

// 커스텀 아이콘 슬롯 — swatch 자리에 임의 노드를 중앙 정렬로 담는다. 아이콘(1em 기준)이 칩 텍스트
// 크기에 맞도록 size별 font-size 를 sizeStyles 에서 준다.
const IconSlot = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  line-height: 0;
`;

const sizeStyles = {
  xs: css`
    height: ${inlineElementHeight.xs};
    gap: 0.35rem;
    padding: 0 0.48rem;

    ${Swatch} {
      width: 0.55rem;
      height: 0.55rem;
      border-radius: 0.22rem;
    }

    ${IconSlot} {
      font-size: ${inlineElementIndicatorSize.xs};
    }
  `,
  sm: css`
    height: ${inlineElementHeight.sm};
    gap: 0.5rem;
    padding: 0 0.68rem;

    ${Swatch} {
      width: 0.7rem;
      height: 0.7rem;
      border-radius: 0.28rem;
    }

    ${IconSlot} {
      font-size: ${inlineElementIndicatorSize.sm};
    }
  `,
  md: css`
    height: ${inlineElementHeight.md};
    gap: 0.55rem;
    padding: 0 0.78rem;

    ${Swatch} {
      width: 0.82rem;
      height: 0.82rem;
      border-radius: 0.32rem;
    }

    ${IconSlot} {
      font-size: ${inlineElementIndicatorSize.md};
    }
  `,
} as const;

const labelChipTextVariants = {
  xs: createLabelChipTextVariant("xs"),
  sm: createLabelChipTextVariant("sm"),
  md: createLabelChipTextVariant("md"),
} satisfies Record<LabelChipSize, TextVariantDefinition>;

// 정적/클릭형 칩이 공유하는 박스. 색(배경/테두리)은 static 은 인라인 style, interactive 는
// interactiveSurfaceCss 가 따로 입힌다.
const chipBase = css<{ $size: LabelChipSize }>`
  box-sizing: border-box;
  display: inline-flex;
  width: fit-content;
  min-width: 0;
  max-width: 100%;
  flex: 0 1 auto;
  align-items: center;
  border: 1px solid;
  ${controlRadius}
  line-height: 1;
  vertical-align: middle;

  ${({ $size }) => sizeStyles[$size]}
`;

// filled/outline — 비인터랙티브 라벨. 색은 인라인 style 로 입혀 평소 외관을 보존한다.
const StaticChip = styled.span<{ $size: LabelChipSize }>`
  ${chipBase}
`;

// interactive — 클릭형 칩. 네이티브 button 으로 렌더해 키보드/포커스/disabled 를 공짜로 얻고,
// hover/active/focus/disabled 진행은 IconButton 과 같은 interactiveSurfaceCss 로 강제한다.
const InteractiveChip = styled.button<{
  $size: LabelChipSize;
  $surface: InteractiveSurfaceSpec;
}>`
  /* 기본 버튼 외형 제거 — 칩 박스 스타일만 남긴다. */
  -webkit-appearance: none;
  appearance: none;
  font: inherit;
  text-align: inherit;

  ${chipBase}
  ${({ $surface }) => interactiveSurfaceCss($surface)}
`;

export function LabelChip({
  color = defaultLabelChipColor,
  tone,
  selected,
  children,
  size = "sm",
  variant = "outline",
  iconPosition = "left",
  icon,
  showIcon = true,
  disabled = false,
  style,
  ...rest
}: LabelChipProps) {
  const theme = useTheme();
  const accent =
    tone === "neutral"
      ? theme.color.text.muted
      : tone
        ? theme.color.dataViz[dataVizKeyOf(tone)]
        : formatLabelChipColorHex(color);
  const colors = buildLabelChipColors(accent);
  const toggle = variant === "interactive" ? selected : undefined;
  const surfaceColors = toggle === false ? buildLabelChipColors(theme.color.text.muted) : colors;
  // 아이콘 우선: icon 노드가 있으면 swatch 대신 그것을, 없으면 showIcon 에 따라 swatch 를 그린다.
  const iconNode =
    icon != null ? (
      <IconSlot aria-hidden="true">{icon}</IconSlot>
    ) : showIcon ? (
      <Swatch aria-hidden="true" style={{ backgroundColor: colors.accent }} />
    ) : null;

  const chipStyle = buildLabelChipStyle(colors, variant, iconPosition, style);
  const label = (
    <Text
      variant={labelChipTextVariants[size]}
      style={
        variant === "interactive" && disabled
          ? disabledTextStyle
          : toggle === false
            ? { color: theme.color.text.muted }
            : undefined
      }
    >
      {children}
    </Text>
  );

  if (variant === "interactive") {
    return (
      <InteractiveChip
        type="button"
        disabled={disabled}
        $size={size}
        $surface={buildChipSurface(surfaceColors, toggle)}
        style={chipStyle}
        {...rest}
        aria-pressed={toggle ?? rest["aria-pressed"]}
      >
        {iconNode}
        {label}
      </InteractiveChip>
    );
  }

  return (
    <StaticChip $size={size} style={chipStyle} {...rest}>
      {iconNode}
      {label}
    </StaticChip>
  );
}

function createLabelChipTextVariant(size: LabelChipSize): TextVariantDefinition {
  return {
    as: TruncatedText,
    style: {
      display: "block",
      minWidth: 0,
      overflow: "hidden",
      fontSize: inlineElementTypography[size].fontSize,
      fontWeight: labelWeight,
      lineHeight: inlineElementTypography[size].lineHeight,
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  };
}

function buildLabelChipStyle(
  colors: LabelChipColors,
  variant: LabelChipVariant,
  iconPosition: LabelChipIconPosition,
  style: CSSProperties | undefined,
): CSSProperties {
  const result: CSSProperties = {
    flexDirection: iconPosition === "right" ? "row-reverse" : "row",
  };
  if (variant === "interactive") {
    // 색(배경/테두리/상태 진행)은 interactiveSurfaceCss 가 소유 — 인라인엔 레이아웃만 둔다.
    // (인라인 background/border-color 는 :hover/:active/:disabled 규칙을 이겨 전환을 막는다.)
    return { ...result, ...style };
  }
  result.backgroundColor = colors.background;
  result.borderColor = variant === "outline" ? colors.border : "transparent";
  return { ...result, ...style };
}

// interactive 칩의 상태 머신 — rest 는 filled 외관(배경 tint, 테두리 없음) → hover 에서 배경이
// 진해지고 칩 색 테두리가 떠오르며 → active 에서 더 진해진다(IconButton 의 surface 사다리 점프를
// 인스턴스 색 알파 단계로 모사). disabled 는 배경을 옅게 죽인다(텍스트 mute 는 disabledTextStyle).
function buildChipSurface(colors: LabelChipColors, selected?: boolean): InteractiveSurfaceSpec {
  return {
    rest: {
      background: colors.background,
      border: selected ? colors.borderActive : "transparent",
      foreground: "inherit",
    },
    hover: {
      background: colors.backgroundHover,
      border: selected ? colors.borderActive : colors.borderHover,
      foreground: "inherit",
    },
    active: {
      background: colors.backgroundActive,
      border: colors.borderActive,
      foreground: "inherit",
    },
    disabled: {
      background: colors.backgroundMuted,
      border: "transparent",
      foreground: "var(--c6-color-text-disabled)",
    },
  };
}

function buildLabelChipColors(accent: string): LabelChipColors {
  // 잉크색: 라벨 원색을 테마 text.default 쪽으로 28% 섞은 파생 — 라이트에선 어두워지고
  // 다크에선 밝아져, 임의 사용자 색이 어느 테마 surface 위에서든 틴트/보더로 읽힌다
  // (raw rgba 알파는 테마 무인지라 다크 네이비 위에서 어두운 라벨색이 죽던 문제의 수렴처).
  // 스와치 fill 만 원색을 유지한다(라벨 색 정체성 표시).
  const ink = `color-mix(in oklab, ${accent} 72%, var(--c6-color-text-default) 28%)`;
  const inkAlpha = (percent: number) => `color-mix(in srgb, ${ink} ${percent}%, transparent)`;
  return {
    accent,
    // 배경 알파 단계: rest(10%) → hover(16%) → active(22%), disabled(5%).
    background: inkAlpha(10),
    backgroundHover: inkAlpha(16),
    backgroundActive: inkAlpha(22),
    backgroundMuted: inkAlpha(5),
    // outline rest 보더는 24% — rest 가장자리는 조용하게(quiet-borders 결정),
    // 상호작용 피드백 단계(hover 36% < active 50%)만 뚜렷하게.
    border: inkAlpha(24),
    borderHover: inkAlpha(36),
    borderActive: inkAlpha(50),
  };
}

function formatLabelChipColorHex(value: number): string {
  return "#" + normalizeLabelChipColor(value).toString(16).padStart(6, "0").toUpperCase();
}

function normalizeLabelChipColor(value: number): number {
  if (!Number.isFinite(value)) {
    return defaultLabelChipColor;
  }
  return Math.max(0, Math.min(maxRgbColor, Math.trunc(value)));
}

LabelChip.displayName = "LabelChip";
